import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Plus, Pencil, Trash2, Check, X, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import { clearOrchestratorContext } from '../hooks/useOrchestratorContext';
import CreateProjectModal from './CreateProjectModal';
import { projectInactiveReason, projectInactiveLabel } from '../utils/projectStatus';
import toast from 'react-hot-toast';

const ProjectSelector: React.FC = () => {
  const { projects, currentProject, setCurrentProject, updateProject, deleteProject, refreshProjects } = useProject();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Inline rename / delete state — only one row can be in either mode at a time.
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameExpireDate, setRenameExpireDate] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  useEffect(() => {
    if (!showDropdown) return;

    // Refresh projects when dropdown opens to get latest is_collaborator flag
    refreshProjects();

    const handleClickOutside = (event: PointerEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    const handleWindowScroll = () => {
      if (window.scrollY !== 0) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('pointerdown', handleClickOutside, true);
    window.addEventListener('scroll', handleWindowScroll, { passive: true });

    return () => {
      document.removeEventListener('pointerdown', handleClickOutside, true);
      window.removeEventListener('scroll', handleWindowScroll);
    };
  }, [showDropdown]);

  // Reset transient row state whenever the dropdown closes so it never
  // reopens mid-edit.
  useEffect(() => {
    if (!showDropdown) {
      setRenamingId(null);
      setRenameValue('');
      setRenameExpireDate('');
      setConfirmDeleteId(null);
    }
  }, [showDropdown]);

  // YYYY-MM-DD helpers (timezone-safe) for the end-date editor
  const todayYMD = (() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
  })();
  const addOneYear = (ymd: string) => {
    const [y, m, d] = ymd.split('-').map(Number);
    return `${y + 1}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  };

  const startEdit = (project: { id: number; name: string; expire_date?: string }) => {
    setConfirmDeleteId(null);
    setRenamingId(project.id);
    setRenameValue(project.name);
    setRenameExpireDate(project.expire_date ? project.expire_date.slice(0, 10) : '');
  };

  const cancelEdit = () => {
    setRenamingId(null);
    setRenameValue('');
    setRenameExpireDate('');
  };

  const submitEdit = async (id: number) => {
    const trimmed = renameValue.trim();
    if (!trimmed) { toast.error('Project name cannot be empty'); return; }
    setBusyId(id);
    const payload: { name: string; expire_date?: string } = { name: trimmed };
    if (renameExpireDate) payload.expire_date = renameExpireDate;
    const ok = await updateProject(id, payload);
    setBusyId(null);
    if (ok) cancelEdit();
    // updateProject already surfaces success / backend-error toasts
  };

  const confirmDelete = async (id: number) => {
    setBusyId(id);
    const ok = await deleteProject(id);
    setBusyId(null);
    if (ok) {
      toast.success('Project deleted');
      setConfirmDeleteId(null);
    } else {
      toast.error('Failed to delete project');
    }
  };

  return (
    <>
      <div className="relative">
        <button
          ref={buttonRef}
          onClick={() => setShowDropdown(!showDropdown)}
          className="flex items-center space-x-2 bg-dusky-purple hover:bg-medium-purple px-4 py-2 rounded-lg transition-colors"
        >
          <div>
            <p className="text-sm font-medium">
              {currentProject?.name || 'Select Project'}
            </p>
            {currentProject?.client_name && (
              <p className="text-xs text-gray-300">{currentProject.client_name}</p>
            )}
          </div>
          <ChevronDown className="w-4 h-4" />
        </button>

        {showDropdown && (
          <div ref={dropdownRef} className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-lg py-2 z-50">
            <div className="px-4 py-2 border-b border-gray-200">
              <button
                onClick={() => {
                  setShowCreateModal(true);
                  setShowDropdown(false);
                }}
                className="flex items-center space-x-2 text-electric-blue hover:text-darker-blue"
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm font-medium">New Project</span>
              </button>
            </div>

            <div className="max-h-64 overflow-y-auto">
              {projects.map((project) => {
                const isRenaming = renamingId === project.id;
                const isConfirmingDelete = confirmDeleteId === project.id;
                const isBusy = busyId === project.id;

                // ── Rename mode ──────────────────────────────────────
                if (isRenaming) {
                  // Max end date = 1 year from the project's CURRENT (saved) expiry,
                  // matching the backend validation; min = today.
                  const editMaxDate = addOneYear(project.expire_date ? project.expire_date.slice(0, 10) : todayYMD);
                  return (
                    <div key={project.id} className="px-4 py-2.5 bg-light-aqua space-y-2">
                      <input
                        autoFocus
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') submitEdit(project.id);
                          if (e.key === 'Escape') cancelEdit();
                        }}
                        disabled={isBusy}
                        placeholder="Project name"
                        className="w-full min-w-0 px-2 py-1 text-sm text-gray-900 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-electric-blue disabled:opacity-60"
                      />
                      <div>
                        <label className="block text-[11px] font-medium text-gray-500 mb-0.5">End date</label>
                        <input
                          type="date"
                          value={renameExpireDate}
                          min={todayYMD}
                          max={editMaxDate}
                          onChange={(e) => setRenameExpireDate(e.target.value)}
                          disabled={isBusy}
                          className="w-full px-2 py-1 text-sm text-gray-900 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-electric-blue disabled:opacity-60"
                        />
                      </div>
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => submitEdit(project.id)}
                          disabled={isBusy}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-electric-blue hover:bg-darker-blue rounded disabled:opacity-60"
                        >
                          {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                          Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          disabled={isBusy}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 rounded disabled:opacity-60"
                        >
                          <X className="w-3.5 h-3.5" /> Cancel
                        </button>
                      </div>
                    </div>
                  );
                }

                // ── Delete confirmation mode ─────────────────────────
                if (isConfirmingDelete) {
                  return (
                    <div key={project.id} className="flex items-center justify-between gap-2 px-4 py-2.5 bg-red-50">
                      <span className="text-sm text-red-700 truncate">
                        Delete "{project.name}"?
                      </span>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => confirmDelete(project.id)}
                          disabled={isBusy}
                          className="px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-60 inline-flex items-center gap-1"
                        >
                          {isBusy && <Loader2 className="w-3 h-3 animate-spin" />}
                          Delete
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          disabled={isBusy}
                          className="px-2 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 rounded disabled:opacity-60"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  );
                }

                // ── Normal row ───────────────────────────────────────
                const inactiveReason = projectInactiveReason(project);
                const isInactive = inactiveReason !== null;
                return (
                  <div
                    key={project.id}
                    className={`group flex items-center justify-between px-4 py-3 hover:bg-gray-50 ${
                      currentProject?.id === project.id ? 'bg-light-aqua' : ''
                    }`}
                  >
                    <button
                      onClick={() => {
                        if (isInactive) {
                          toast.error(`"${project.name}" is ${projectInactiveLabel(inactiveReason).toLowerCase()} and can't be set as the active project. Restore it from its end date to use it again.`);
                          return;
                        }
                        if (currentProject && currentProject.id !== project.id) {
                          clearOrchestratorContext();
                        }
                        setCurrentProject(project);
                        setShowDropdown(false);
                      }}
                      className={`flex-1 min-w-0 text-left ${isInactive ? 'cursor-not-allowed' : ''}`}
                      title={isInactive ? `${projectInactiveLabel(inactiveReason)} — cannot be selected` : undefined}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <p className={`text-sm font-medium truncate ${isInactive ? 'text-gray-400' : 'text-gray-900'}`}>{project.name}</p>
                        {isInactive && (
                          <span className="bg-gray-200 text-gray-600 px-2 py-0.5 rounded text-xs font-medium shrink-0">
                            {projectInactiveLabel(inactiveReason)}
                          </span>
                        )}
                        {project.is_collaborator && (
                          <span className="bg-green-500 text-white px-2 py-0.5 rounded text-xs font-medium shrink-0">
                            Collaborator
                          </span>
                        )}
                      </div>
                      {project.client_name && (
                        <p className={`text-xs truncate ${isInactive ? 'text-gray-400' : 'text-gray-500'}`}>{project.client_name}</p>
                      )}
                      {project.collaborator_names && project.collaborator_names.length > 0 && (
                        <p className="flex items-center gap-1 mt-0.5 truncate">
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-purple-500">Collaborators</span>
                          <span className="text-[10px] text-gray-400">{project.collaborator_names.join(' · ')}</span>
                        </p>
                      )}
                    </button>
                    <div className="flex items-center gap-1 shrink-0 ml-2">
                      <span className="text-xs text-electric-blue">{project.progress}%</span>
                      <button
                        onClick={() => startEdit(project)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-gray-400 hover:text-electric-blue hover:bg-gray-100 rounded"
                        title="Edit project"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => { setRenamingId(null); setConfirmDeleteId(project.id); }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Delete project"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <CreateProjectModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />
    </>
  );
};

export default ProjectSelector;
